import { Component, OnInit } from '@angular/core';
import { BaseComponent } from './core/base-component';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import * as _ from 'lodash';
import { AuthenticationService } from './auth/authentication/authentication.service';
import { Store } from '@ngrx/store';
import { AppState } from './app-state';
import { selectUserState, UserState } from './auth/user-state';
import { AnalyticsService } from './google-analytics/analytics.service';
import { NavigationState, selectNavigationState } from './navigation/navigation-state';
import { WizardRunnerService } from './core/wizard/wizard-runner.service';
import { EmailVerificationWizard } from './core/email-verification/email-verification-wizard';
import { filter } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { FilterStatusService } from './core/services/filter-status.service';

@Component({
  standalone: false,
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent extends BaseComponent implements OnInit {

  isAuthenticated = false;
  isNavigationCollapsed = true;
  isNoPadRoute = false;
  isOnMaintenancePage = false;
  isOnMapSearchPage = false;
  filtersApplied = false;
  filterCount = 0;

  private noPadRoutes: string[] = [
    '/map-search',
    '/detail',
    '/maintenance'
  ];

  constructor(private activatedRoute: ActivatedRoute,
              private analyticsService: AnalyticsService,
              private authenticationService: AuthenticationService,
              private router: Router,
              private ngrxStore: Store<AppState>,
              private translate: TranslateService,
              private wizardRunner: WizardRunnerService,
              private filterStatusService: FilterStatusService) {
    super();
    this.translate.setDefaultLang('en');
  }

  ngOnInit() {
    this.isOnMaintenancePage = window.location.pathname === '/maintenance';

    this.analyticsService.init();
    this.subscribeToRouteChange();
    this.subscribeToAuthState();
    this.subscribeToNavigationState();
    this.subscribeToFilterStatus();
    this.loadAuthenticationFromStorage();
  }

  private handleEmailVerification(event: NavigationEnd): void {
    const containsEmailParam = event.url.indexOf('email=') !== -1;
    if (containsEmailParam) {
      //Remove all Auth0 query parameters
      this.router.navigate([], {relativeTo: this.activatedRoute, queryParams: {}});

      this.wizardRunner.run(new EmailVerificationWizard());
    }
  }

  private loadAuthenticationFromStorage(): void {
    this.authenticationService.loadAuthenticationFromStorage();
  }

  private subscribeToAuthState(): void {
    this.addSubscription(
      selectUserState(this.ngrxStore)
        .subscribe((state: UserState) => {
          this.isAuthenticated = !!state.currentUser;
        })
    );
  }

  private subscribeToNavigationState(): void {
    this.addSubscription(
      selectNavigationState(this.ngrxStore)
        .subscribe((state: NavigationState) => {
          this.isNavigationCollapsed = state.isCollapsed;
        })
    );
  }

  private subscribeToFilterStatus(): void {
    this.addSubscription(
      this.filterStatusService.filtersApplied$
        .subscribe((applied: boolean) => {
          this.filtersApplied = applied;
        })
    );
    
    this.addSubscription(
      this.filterStatusService.filterCount$
        .subscribe((count: number) => {
          this.filterCount = count;
        })
    );
  }

  switchLanguage(language: string) {
    this.translate.use(language);
  }

  private subscribeToRouteChange(): void {
    this.addSubscription(
      this.router.events
        .pipe(filter((event: any) => event instanceof NavigationEnd))
        .subscribe((event: NavigationEnd) => {
          this.isOnMaintenancePage = window.location.pathname === '/maintenance' || event.urlAfterRedirects === '/maintenance';
          this.isOnMapSearchPage = event.url.indexOf('/map-search') === 0;
          this.handleEmailVerification(event);
          this.isNoPadRoute = !!_.find(this.noPadRoutes, (route: string) => {
            return event.url.indexOf(route) === 0;
          });
        })
    );
  }

}
